import { c as _c } from "react/compiler-runtime";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
export type GalleryItem = {
  type: "photo";
  src: string;
} | {
  type: "video";
  src: string;
};

/** Пропорции кадра галереи (ширина / высота) - те же, что у `aspect-[560/728]` ниже. */
export const GALLERY_FRAME_RATIO = 560 / 728;
const HIDE_SCROLLBAR = "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

/** Индекс слайда по прокрутке ленты (ближайший к левому краю). */
export const slideIndexAt = (scrollLeft: number, slideWidth: number, count: number): number => {
  if (slideWidth <= 0 || count <= 0) return 0;
  return Math.min(count - 1, Math.max(0, Math.round(scrollLeft / slideWidth)));
};

/**
 * Галерея карточки товара: фото листаются свайпом (и прокруткой тачпада)
 * лентой с прилипанием к слайду, как карусель в мобильном клиенте. Точки
 * внизу показывают и переключают слайд, на десктопе при наведении — стрелки.
 * Нажатие на фото открывает его на весь экран (onOpen). Индекс управляемый:
 * смена варианта сбрасывает галерею на первое фото.
 */
export function ProductGallery(t0) {
  const $ = _c(42);
  const {
    items,
    active,
    onActive,
    onOpen,
    alt,
    renderVideo,
    renderOverlay,
    children
  } = t0;
  const {
    t
  } = useTranslation();
  const track = useRef(null);
  const count = items.length;
  const settle = useRef(undefined);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => clearTimeout(settle.current);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const shown = useRef(active);
  let t3;
  let t4;
  if ($[2] !== active) {
    t3 = () => {
      const el = track.current;
      if (!el || shown.current === active) {
        return;
      }
      shown.current = active;
      el.scrollTo({
        left: active * el.clientWidth,
        behavior: "smooth"
      });
    };
    t4 = [active];
    $[2] = active;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== count || $[6] !== onActive) {
    t5 = () => {
      clearTimeout(settle.current);
      settle.current = setTimeout(() => {
        const el_0 = track.current;
        if (!el_0) {
          return;
        }
        const i = slideIndexAt(el_0.scrollLeft, el_0.clientWidth, count);
        if (i !== shown.current) {
          shown.current = i;
          onActive(i);
        }
      }, 90);
    };
    $[5] = count;
    $[6] = onActive;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onScroll = t5;
  let t6;
  if ($[8] !== active || $[9] !== count || $[10] !== onActive) {
    t6 = delta => onActive(Math.min(count - 1, Math.max(0, active + delta)));
    $[8] = active;
    $[9] = count;
    $[10] = onActive;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const go = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("flex size-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain", HIDE_SCROLLBAR);
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== alt || $[14] !== count || $[15] !== items || $[16] !== onOpen || $[17] !== renderOverlay || $[18] !== renderVideo || $[19] !== t) {
    let t9;
    if ($[21] !== alt || $[22] !== count || $[23] !== onOpen || $[24] !== renderOverlay || $[25] !== renderVideo || $[26] !== t) {
      t9 = (item, i_0) => <div key={`${item.type}-${item.src}-${i_0}`} className="relative size-full shrink-0 snap-center snap-always" aria-roledescription="slide" aria-label={t("product.slideOf", {
        defaultValue: "\u0424\u043E\u0442\u043E {{n}} \u0438\u0437 {{count}}",
        n: i_0 + 1,
        count
      })}>{item.type === "photo" ? <button type="button" onClick={() => onOpen(i_0)} aria-label={t("product.openPhoto", {
          defaultValue: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0444\u043E\u0442\u043E \u043D\u0430 \u0432\u0435\u0441\u044C \u044D\u043A\u0440\u0430\u043D"
        })} className="block size-full cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-osmix-tangelo/40"><img src={item.src} alt={i_0 === 0 ? alt : ""} draggable={false} loading={i_0 === 0 ? "eager" : "lazy"} className="size-full object-cover" /></button> : renderVideo(item.src)}{renderOverlay?.(item, i_0)}</div>;
      $[21] = alt;
      $[22] = count;
      $[23] = onOpen;
      $[24] = renderOverlay;
      $[25] = renderVideo;
      $[26] = t;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    t8 = items.map(t9);
    $[13] = alt;
    $[14] = count;
    $[15] = items;
    $[16] = onOpen;
    $[17] = renderOverlay;
    $[18] = renderVideo;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[28] !== onScroll || $[29] !== t8) {
    t9 = <div ref={track} onScroll={onScroll} data-testid="product-gallery" className={t7}>{t8}</div>;
    $[28] = onScroll;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== active || $[32] !== count || $[33] !== go || $[34] !== items || $[35] !== onActive || $[36] !== t) {
    t10 = count > 1 && <><button type="button" aria-label={t("common.prev", {
        defaultValue: "\u041D\u0430\u0437\u0430\u0434"
      })} onClick={() => go(-1)} disabled={active === 0} className={cn("absolute top-1/2 hidden size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-osmix-text-1 opacity-0 shadow-sm backdrop-blur outline-none transition-opacity group-hover/gallery:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 disabled:pointer-events-none disabled:opacity-0! [@media(hover:hover)]:flex", "left-3")}><ChevronLeft className="size-5" /></button><button type="button" aria-label={t("common.next", {
        defaultValue: "\u0412\u043F\u0435\u0440\u0451\u0434"
      })} onClick={() => go(1)} disabled={active >= count - 1} className={cn("absolute top-1/2 hidden size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-osmix-text-1 opacity-0 shadow-sm backdrop-blur outline-none transition-opacity group-hover/gallery:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 disabled:pointer-events-none disabled:opacity-0! [@media(hover:hover)]:flex", "right-3")}><ChevronRight className="size-5" /></button><div className="pointer-events-none absolute inset-x-0 bottom-2.5 flex items-center justify-center gap-2">{items.map((item_0, i_1) => <button key={`dot-${item_0.type}-${item_0.src}-${i_1}`} type="button" onClick={() => onActive(i_1)} aria-label={t("product.viewImage", {
          defaultValue: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u0438\u0437\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u0435 {{n}}",
          n: i_1 + 1
        })} aria-current={i_1 === active} className={cn("pointer-events-auto size-1.5 rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40", i_1 === active ? "bg-white/65" : "bg-white/25 hover:bg-white/65")} />)}</div></>;
    $[31] = active;
    $[32] = count;
    $[33] = go;
    $[34] = items;
    $[35] = onActive;
    $[36] = t;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== children || $[39] !== t10 || $[40] !== t9) {
    t11 = <div className="group/gallery relative aspect-[560/728] overflow-hidden rounded-[4px] bg-osmix-gray-100">{t9}{children}{t10}</div>;
    $[38] = children;
    $[39] = t10;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
}